import React, { useEffect, useState } from "react";
import { Line, Bar } from "react-chartjs-2";

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Label,
  FormGroup,
  Input,
  Table,
  Row,
  Col,
  UncontrolledTooltip,
} from "reactstrap";

import {
  chartExample1,
  chartExample2,
  chartExample3,
  chartExample4,
} from "variables/charts.js";

import ButtonGroupComponent from "components/Buttons/ButtonGroups";
import { useTranslation } from "../hooks/useTranslation";
import { useFetchDashboardSalesQuery } from "../api/analyticsApi";

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { dateFrom: toDateInputValue(start), dateTo: toDateInputValue(end) };
};

const formatMoney = (value) =>
  new Intl.NumberFormat("sq-XK", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatChartDate = (value) => {
  const [, month, day] = value.split("-");
  return `${day}.${month}`;
};

const buildDashboardChartData = (daily, field, label, color) => ({
  labels: daily.map((point) => formatChartDate(point.date)),
  datasets: [
    {
      label,
      data: daily.map((point) => point[field]),
      borderColor: color,
      backgroundColor: `${color}1f`,
      borderWidth: 2,
      pointRadius: daily.length > 45 ? 0 : 3,
      pointHoverRadius: 5,
      tension: 0.32,
      fill: true,
    },
  ],
});

const dashboardChartOptions = (currency = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: {
    legend: { display: false },
    tooltip: currency
      ? {
          callbacks: {
            label: (context) => formatMoney(context.parsed.y),
          },
        }
      : undefined,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxTicksLimit: 9, color: "#8b92a9" },
    },
    y: {
      beginAtZero: !currency,
      grid: { color: "rgba(255, 255, 255, 0.06)" },
      ticks: {
        color: "#8b92a9",
        precision: currency ? undefined : 0,
        callback: currency ? (value) => `${value} €` : undefined,
      },
    },
  },
});

function Dashboard(props) {
  const t = useTranslation();
  const [rangeDraft, setRangeDraft] = useState(getDefaultRange);
  const [salesRange, setSalesRange] = useState(getDefaultRange);
  const rangeIsInvalid = rangeDraft.dateFrom > rangeDraft.dateTo;
  const {
    data: dashboardSales,
    isFetching: dashboardSalesLoading,
    isError: dashboardSalesError,
  } = useFetchDashboardSalesQuery(salesRange);
  const [shipmentsChartData, setShipmentsChartData] = useState(null);
  const [quantityChartData, setQuantityChartData] = useState(null);
  const [onlineShipmentsChartData, setOnlineShipmentsChartData] =
    useState(null);
  const [bankPaymentChartData, setBankPaymentChartData] = useState(null);

  const [activeButton, setActiveButton] = useState("sales");
  const today = new Date();
  const year = today.getFullYear();
  const handleButtonClick = (button) => {
    const currentYears = [year];
    fetchShipmentsChartData({ type: button, years: currentYears });
    setActiveButton(button);
  };

  const fetchShipmentsChartData = async ({ type, years }) => {
    try {
      const data1 = await chartExample1.data1({
        canvas: document.createElement("canvas"),
        type,
        years: years,
      });
      setShipmentsChartData(data1);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  const fetchQuantityChartData = async ({ type, year }) => {
    try {
      const data1 = await chartExample2.data1({
        type,
        canvas: document.createElement("canvas"),
        year,
      });
      setQuantityChartData({ data1, totalQuantity: data1.totalQuantity });
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  const fetchBankPaymentChartData = async ({ type, year }) => {
    try {
      const data1 = await chartExample4.data1({
        type,
        canvas: document.createElement("canvas"),
        year,
      });
      setBankPaymentChartData({
        data1,
        totalBankPayment: data1.totalBankPayment,
      });
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  const fetchOnlineShipmentsChartData = async ({ type, year }) => {
    try {
      const data1 = await chartExample3.data1({
        type,
        canvas: document.createElement("canvas"),
        year,
      });

      setOnlineShipmentsChartData({
        data1,
        totalQuantity: data1.totalQuantity,
      });
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  useEffect(() => {
    fetchShipmentsChartData({ type: "Default", years: [year] });
    fetchQuantityChartData({ type: "quantity", year: [year] });
    fetchOnlineShipmentsChartData({ type: "onlinePrices", year: [year] });
    fetchBankPaymentChartData({ type: "bankPrices", year: [year] });
  }, [year]);

  return (
    <>
      <div className="content">
        <section className="dashboard-sales" aria-labelledby="dashboard-sales-title">
          <div className="dashboard-sales__toolbar">
            <div>
              <p className="dashboard-sales__eyebrow">{t("dashboard.salesOverview")}</p>
              <h1 id="dashboard-sales-title">{t("dashboard.salesByDate")}</h1>
              <p>{t("dashboard.salesByDateHint")}</p>
            </div>
            <form
              className="dashboard-sales__range"
              onSubmit={(event) => {
                event.preventDefault();
                if (!rangeIsInvalid) setSalesRange(rangeDraft);
              }}
            >
              <label>
                <span>{t("dashboard.dateFrom")}</span>
                <Input
                  type="date"
                  value={rangeDraft.dateFrom}
                  max={rangeDraft.dateTo}
                  onChange={(event) =>
                    setRangeDraft((current) => ({
                      ...current,
                      dateFrom: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                <span>{t("dashboard.dateTo")}</span>
                <Input
                  type="date"
                  value={rangeDraft.dateTo}
                  min={rangeDraft.dateFrom}
                  onChange={(event) =>
                    setRangeDraft((current) => ({
                      ...current,
                      dateTo: event.target.value,
                    }))
                  }
                />
              </label>
              <Button color="primary" type="submit" disabled={rangeIsInvalid}>
                {dashboardSalesLoading
                  ? t("dashboard.loadingSales")
                  : t("dashboard.applyRange")}
              </Button>
            </form>
          </div>

          {dashboardSalesError ? (
            <div className="dashboard-sales__error" role="alert">
              {t("dashboard.salesLoadError")}
            </div>
          ) : (
            <Row>
              <Col lg="6">
                <div className="dashboard-sales__metric">
                  <div className="dashboard-sales__metric-header">
                    <div>
                      <span>{t("dashboard.salesCount")}</span>
                      <strong>{dashboardSales?.salesCount ?? "—"}</strong>
                    </div>
                    <span className="dashboard-sales__unit">{t("dashboard.completedSales")}</span>
                  </div>
                  <div className="dashboard-sales__chart">
                    {dashboardSales && (
                      <Line
                        data={buildDashboardChartData(
                          dashboardSales.daily,
                          "salesCount",
                          t("dashboard.salesCount"),
                          "#1d8cf8"
                        )}
                        options={dashboardChartOptions(false)}
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col lg="6">
                <div className="dashboard-sales__metric dashboard-sales__metric--net">
                  <div className="dashboard-sales__metric-header">
                    <div>
                      <span>{t("dashboard.netTotal")}</span>
                      <strong>{dashboardSales ? formatMoney(dashboardSales.netTotal) : "—"}</strong>
                    </div>
                    <span className="dashboard-sales__unit">{t("dashboard.afterReturns")}</span>
                  </div>
                  <div className="dashboard-sales__breakdown">
                    <span>
                      {t("dashboard.grossTotal")} <b>{formatMoney(dashboardSales?.grossTotal)}</b>
                    </span>
                    <span>
                      {t("dashboard.returnsTotal")} <b>−{formatMoney(dashboardSales?.returnsTotal)}</b>
                    </span>
                  </div>
                  <div className="dashboard-sales__chart">
                    {dashboardSales && (
                      <Line
                        data={buildDashboardChartData(
                          dashboardSales.daily,
                          "netTotal",
                          t("dashboard.netTotal"),
                          "#00bf9a"
                        )}
                        options={dashboardChartOptions(true)}
                      />
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </section>
        <Row>
          <Col xs="12">
            <Card className="card-chart">
              <CardHeader>
                <Row>
                  <Col className="text-left" sm="4">
                    <h5 className="card-category">
                      {t("dashboard.totalShipments")}
                    </h5>
                    <CardTitle tag="h2">{t("dashboard.performance")}</CardTitle>
                  </Col>
                  <Col sm="12" md="4" className="mt-3 mt-md-0 ">
                    {/* <YearRangePicker onYearsChange={onYearsChange} /> */}
                  </Col>
                  <Col sm="4">
                    <ButtonGroupComponent
                      activeButton={activeButton}
                      onButtonClick={handleButtonClick}
                      buttons={["sales", "shoes", "textile"]}
                    />
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart-area">
                  {shipmentsChartData && (
                    <Line
                      data={shipmentsChartData}
                      options={chartExample1.options}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col lg="4">
            <Card className="card-chart">
              <CardHeader>
                <h5 className="card-category">
                  {t("dashboard.totalShipments")}
                </h5>
                <CardTitle tag="h3">
                  <i className="tim-icons icon-bell-55 text-info" />{" "}
                  {quantityChartData ? quantityChartData.totalQuantity : 0}{" "}
                  {t("dashboard.qty")}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="chart-area">
                  {quantityChartData && (
                    <Line
                      data={quantityChartData.data1}
                      options={chartExample2.options}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg="4">
            <Card className="card-chart">
              <CardHeader>
                <h5 className="card-category">{t("dashboard.onlineSales")}</h5>
                <CardTitle tag="h3">
                  <i className="tim-icons icon-delivery-fast text-primary" />{" "}
                  {onlineShipmentsChartData
                    ? onlineShipmentsChartData.totalQuantity
                    : 0}
                  {"€"}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="chart-area">
                  {onlineShipmentsChartData && (
                    <Bar
                      data={onlineShipmentsChartData.data1}
                      options={chartExample3.options}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg="4">
            <Card className="card-chart">
              <CardHeader>
                <h5 className="card-category">{t("dashboard.bankPayments")}</h5>
                <CardTitle tag="h3">
                  <i className="tim-icons icon-bank text-success" />
                  {bankPaymentChartData
                    ? bankPaymentChartData.totalBankPayment
                    : 0}
                  {"€"}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="chart-area">
                  {bankPaymentChartData && (
                    <Line
                      data={bankPaymentChartData.data1}
                      options={chartExample4.options}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col lg="6" md="12">
            <Card className="card-tasks">
              <CardHeader>
                <h6 className="title d-inline">{t("dashboard.tasks")}(5)</h6>
                <p className="card-category d-inline">
                  {" "}
                  {t("dashboard.today")}
                </p>
                <UncontrolledDropdown>
                  <DropdownToggle
                    caret
                    className="btn-icon"
                    color="link"
                    data-toggle="dropdown"
                    type="button"
                  >
                    <i className="tim-icons icon-settings-gear-63" />
                  </DropdownToggle>
                  <DropdownMenu aria-labelledby="dropdownMenuLink" right>
                    <DropdownItem
                      href="#pablo"
                      onClick={(e) => e.preventDefault()}
                    >
                      {t("dashboard.action")}
                    </DropdownItem>
                    <DropdownItem
                      href="#pablo"
                      onClick={(e) => e.preventDefault()}
                    >
                      {t("dashboard.anotherAction")}
                    </DropdownItem>
                    <DropdownItem
                      href="#pablo"
                      onClick={(e) => e.preventDefault()}
                    >
                      {t("dashboard.somethingElse")}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </CardHeader>
              <CardBody>
                <div className="table-full-width table-responsive">
                  <Table>
                    <tbody>
                      <tr>
                        <td>
                          <FormGroup check>
                            <Label check>
                              <Input defaultValue="" type="checkbox" />
                              <span className="form-check-sign">
                                <span className="check" />
                              </span>
                            </Label>
                          </FormGroup>
                        </td>
                        <td>
                          <p className="title">Update the Documentation</p>
                          <p className="text-muted">
                            Dwuamish Head, Seattle, WA 8:47 AM
                          </p>
                        </td>
                        <td className="td-actions text-right">
                          <Button
                            color="link"
                            id="tooltip636901683"
                            title=""
                            type="button"
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <UncontrolledTooltip
                            delay={0}
                            target="tooltip636901683"
                            placement="right"
                          >
                            {t("dashboard.editTask")}
                          </UncontrolledTooltip>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <FormGroup check>
                            <Label check>
                              <Input
                                defaultChecked
                                defaultValue=""
                                type="checkbox"
                              />
                              <span className="form-check-sign">
                                <span className="check" />
                              </span>
                            </Label>
                          </FormGroup>
                        </td>
                        <td>
                          <p className="title">GDPR Compliance</p>
                          <p className="text-muted">
                            The GDPR is a regulation that requires businesses to
                            protect the personal data and privacy of Europe
                            citizens for transactions that occur within EU
                            member states.
                          </p>
                        </td>
                        <td className="td-actions text-right">
                          <Button
                            color="link"
                            id="tooltip457194718"
                            title=""
                            type="button"
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <UncontrolledTooltip
                            delay={0}
                            target="tooltip457194718"
                            placement="right"
                          >
                            Edit Task
                          </UncontrolledTooltip>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <FormGroup check>
                            <Label check>
                              <Input defaultValue="" type="checkbox" />
                              <span className="form-check-sign">
                                <span className="check" />
                              </span>
                            </Label>
                          </FormGroup>
                        </td>
                        <td>
                          <p className="title">Solve the issues</p>
                          <p className="text-muted">
                            Fifty percent of all respondents said they would be
                            more likely to shop at a company
                          </p>
                        </td>
                        <td className="td-actions text-right">
                          <Button
                            color="link"
                            id="tooltip362404923"
                            title=""
                            type="button"
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <UncontrolledTooltip
                            delay={0}
                            target="tooltip362404923"
                            placement="right"
                          >
                            Edit Task
                          </UncontrolledTooltip>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <FormGroup check>
                            <Label check>
                              <Input defaultValue="" type="checkbox" />
                              <span className="form-check-sign">
                                <span className="check" />
                              </span>
                            </Label>
                          </FormGroup>
                        </td>
                        <td>
                          <p className="title">Release v2.0.0</p>
                          <p className="text-muted">
                            Ra Ave SW, Seattle, WA 98116, SUA 11:19 AM
                          </p>
                        </td>
                        <td className="td-actions text-right">
                          <Button
                            color="link"
                            id="tooltip818217463"
                            title=""
                            type="button"
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <UncontrolledTooltip
                            delay={0}
                            target="tooltip818217463"
                            placement="right"
                          >
                            Edit Task
                          </UncontrolledTooltip>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <FormGroup check>
                            <Label check>
                              <Input defaultValue="" type="checkbox" />
                              <span className="form-check-sign">
                                <span className="check" />
                              </span>
                            </Label>
                          </FormGroup>
                        </td>
                        <td>
                          <p className="title">Export the processed files</p>
                          <p className="text-muted">
                            The report also shows that consumers will not easily
                            forgive a company once a breach exposing their
                            personal data occurs.
                          </p>
                        </td>
                        <td className="td-actions text-right">
                          <Button
                            color="link"
                            id="tooltip831835125"
                            title=""
                            type="button"
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <UncontrolledTooltip
                            delay={0}
                            target="tooltip831835125"
                            placement="right"
                          >
                            Edit Task
                          </UncontrolledTooltip>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <FormGroup check>
                            <Label check>
                              <Input defaultValue="" type="checkbox" />
                              <span className="form-check-sign">
                                <span className="check" />
                              </span>
                            </Label>
                          </FormGroup>
                        </td>
                        <td>
                          <p className="title">Arival at export process</p>
                          <p className="text-muted">
                            Capitol Hill, Seattle, WA 12:34 AM
                          </p>
                        </td>
                        <td className="td-actions text-right">
                          <Button
                            color="link"
                            id="tooltip217595172"
                            title=""
                            type="button"
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <UncontrolledTooltip
                            delay={0}
                            target="tooltip217595172"
                            placement="right"
                          >
                            Edit Task
                          </UncontrolledTooltip>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg="6" md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">{t("dashboard.simpleTable")}</CardTitle>
              </CardHeader>
              <CardBody>
                <Table className="tablesorter" responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>{t("table.name")}</th>
                      <th>{t("table.country")}</th>
                      <th>{t("table.city")}</th>
                      <th className="text-center">{t("table.salary")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Dakota Rice</td>
                      <td>Niger</td>
                      <td>Oud-Turnhout</td>
                      <td className="text-center">$36,738</td>
                    </tr>
                    <tr>
                      <td>Minerva Hooper</td>
                      <td>Curaçao</td>
                      <td>Sinaai-Waas</td>
                      <td className="text-center">$23,789</td>
                    </tr>
                    <tr>
                      <td>Sage Rodriguez</td>
                      <td>Netherlands</td>
                      <td>Baileux</td>
                      <td className="text-center">$56,142</td>
                    </tr>
                    <tr>
                      <td>Philip Chaney</td>
                      <td>Korea, South</td>
                      <td>Overland Park</td>
                      <td className="text-center">$38,735</td>
                    </tr>
                    <tr>
                      <td>Doris Greene</td>
                      <td>Malawi</td>
                      <td>Feldkirchen in Kärnten</td>
                      <td className="text-center">$63,542</td>
                    </tr>
                    <tr>
                      <td>Mason Porter</td>
                      <td>Chile</td>
                      <td>Gloucester</td>
                      <td className="text-center">$78,615</td>
                    </tr>
                    <tr>
                      <td>Jon Porter</td>
                      <td>Portugal</td>
                      <td>Gloucester</td>
                      <td className="text-center">$98,615</td>
                    </tr>
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Dashboard;
